import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Plus, FileText, Upload, CalendarDays, List, Settings } from 'lucide-react';
import { DeadlineCalendar } from './DeadlineCalendar';
import { CalendarNotificationSettings } from './CalendarNotificationSettings';
import { DeadlineList } from './DeadlineList';
import { DeadlineForm } from './DeadlineForm';
import { TemplateSelector } from './TemplateSelector';
import { DocumentImporter } from './DocumentImporter';
import { ExtractionResultsForm, type ExtractionFormData } from './ExtractionResultsForm';
import { useDeadlines } from '@/hooks/useDeadlines';
import type { Deadline, DeadlineTemplate, CreateDeadlineInput } from '@/types/deadlines';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface DeadlineCalendarModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ModalView = 'calendar' | 'list' | 'form' | 'templates' | 'import' | 'extraction' | 'settings';

export function DeadlineCalendarModal({
  open,
  onOpenChange,
}: DeadlineCalendarModalProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { 
    deadlines, 
    templates, 
    isLoading,
    create, 
    createFromTemplate,
    update, 
    remove, 
    complete 
  } = useDeadlines();

  const [view, setView] = useState<ModalView>('calendar');
  const [selectedDeadline, setSelectedDeadline] = useState<Deadline | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Deadline | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Estado para importación de documentos
  const [importedFile, setImportedFile] = useState<File | null>(null);
  const [extractedText, setExtractedText] = useState<string>('');

  const handleCreateNew = () => {
    setSelectedDeadline(null);
    setView('form');
  };

  const handleShowTemplates = () => {
    setView('templates');
  };

  const handleEdit = (deadline: Deadline) => {
    setSelectedDeadline(deadline);
    setView('form');
  };

  const handleFormSubmit = async (data: CreateDeadlineInput) => {
    setIsSubmitting(true);
    try {
      if (selectedDeadline) {
        await update(selectedDeadline.id, data);
        toast({ title: 'Plazo actualizado correctamente' });
      } else {
        await create(data);
        toast({ title: 'Plazo creado correctamente' });
      }
      setView('calendar');
      setSelectedDeadline(null);
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'No se pudo guardar el plazo',
        variant: 'destructive' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTemplateSelect = async (template: DeadlineTemplate) => {
    setIsSubmitting(true);
    try {
      await createFromTemplate(template);
      toast({ title: 'Plazo creado desde plantilla' });
      setView('calendar');
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'No se pudo crear el plazo',
        variant: 'destructive' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComplete = async (deadline: Deadline) => {
    try {
      await complete(deadline.id);
      toast({ title: 'Plazo marcado como completado' });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'No se pudo actualizar el plazo',
        variant: 'destructive' 
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    
    try {
      await remove(deleteTarget.id);
      toast({ title: 'Plazo eliminado' });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'No se pudo eliminar el plazo',
        variant: 'destructive' 
      });
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleViewChat = (chatId: string) => {
    onOpenChange(false);
    navigate(`/app/chat/${chatId}`);
  };

  const handleDateClick = (date: Date, dateDeadlines: Deadline[]) => {
    if (dateDeadlines.length > 0) {
      // Si hay plazos ese día, cambiar a vista lista filtrada
      setView('list');
    }
  };

  const goBack = () => {
    setView('calendar');
    setSelectedDeadline(null);
    setImportedFile(null);
    setExtractedText('');
  };

  // Cuando el documento se procesa y se extrae el texto
  const handleFileProcessed = (file: File, text: string) => {
    setImportedFile(file);
    setExtractedText(text);
    setView('extraction');
  };

  // Cuando se confirma la creación desde documento importado
  const handleExtractionSubmit = async (data: ExtractionFormData) => {
    setIsSubmitting(true);
    try {
      await create({
        title: data.title,
        description: data.description,
        due_date: data.dueDate,
        original_due_date: data.originalDueDate || data.dueDate,
        category: data.category,
        priority: data.priority,
        day_type: data.dayType,
        expediente_number: data.expedienteNumber,
        interested_party: data.interestedParty,
        reminder_days: data.reminderDays,
        source_type: 'document',
      });
      toast({ title: 'Expediente creado desde documento' });
      goBack();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'No se pudo crear el expediente',
        variant: 'destructive' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Calendario de Plazos
            </DialogTitle>
          </DialogHeader>

          {view === 'calendar' || view === 'list' ? (
            <>
              {/* Barra de acciones */}
              <div className="flex items-center gap-2 pb-4 border-b">
                <Button onClick={handleCreateNew} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo
                </Button>
                <Button variant="outline" onClick={handleShowTemplates} size="sm">
                  <FileText className="h-4 w-4 mr-2" />
                  Plantillas
                </Button>
                <Button variant="outline" onClick={() => setView('import')} size="sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Importar
                </Button>
                <div className="flex-1" />
                <Button variant="ghost" onClick={() => setView('settings')} size="sm" title="Configurar avisos">
                  <Settings className="h-4 w-4" />
                </Button>
              </div>

              {/* Tabs de vista */}
              <Tabs value={view} onValueChange={(v) => setView(v as ModalView)} className="flex-1 min-h-0">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="calendar" className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    Calendario
                  </TabsTrigger>
                  <TabsTrigger value="list" className="flex items-center gap-2">
                    <List className="h-4 w-4" />
                    Lista
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="calendar" className="mt-0">
                  <DeadlineCalendar
                    deadlines={deadlines}
                    onDateClick={handleDateClick}
                    onDeadlineClick={handleEdit}
                  />
                </TabsContent>

                <TabsContent value="list" className="mt-0">
                  <DeadlineList
                    deadlines={deadlines}
                    onEdit={handleEdit}
                    onDelete={setDeleteTarget}
                    onComplete={handleComplete}
                    onViewChat={handleViewChat}
                  />
                </TabsContent>
              </Tabs>
            </>
          ) : view === 'settings' ? (
            <div className="flex-1 overflow-auto">
              <div className="flex items-center gap-2 mb-4">
                <Button variant="ghost" size="sm" onClick={goBack}>
                  ← Volver
                </Button>
              </div>
              <CalendarNotificationSettings />
            </div>
          ) : view === 'form' ? (
            <div className="flex-1 overflow-auto">
              <DeadlineForm
                deadline={selectedDeadline || undefined}
                onSubmit={handleFormSubmit}
                onCancel={goBack}
                isLoading={isSubmitting}
              />
            </div>
          ) : view === 'templates' ? (
            <div className="flex-1 overflow-auto">
              <TemplateSelector
                templates={templates}
                onSelect={handleTemplateSelect}
                onCancel={goBack}
                isLoading={isSubmitting}
              />
            </div>
          ) : view === 'import' ? (
            <div className="flex-1 overflow-auto">
              <DocumentImporter
                onFileProcessed={handleFileProcessed}
                onCancel={goBack}
                isProcessing={isSubmitting}
              />
            </div>
          ) : view === 'extraction' && importedFile && extractedText ? (
            <div className="flex-1 overflow-auto">
              <ExtractionResultsForm
                file={importedFile}
                extractedText={extractedText}
                onSubmit={handleExtractionSubmit}
                onCancel={goBack}
                isLoading={isSubmitting}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmación de eliminación */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar plazo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el plazo 
              "{deleteTarget?.title}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
